import React, { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import ButtonBase from "@mui/material/ButtonBase";

import Button from "components/Button";
import EmptyBlock from "components/EmptyBlock";

import TokenDeleteModal from "./TokenDeleteModal";
import TokenUpdateModal from "./TokenUpdateModal";

import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  createClientApiKey,
  getClientApiKey,
} from "store/features/clientApiKeySlice";
import { setNotification } from "store/features/notificationSlice";

import { ReactComponent as CopyIcon } from "assets/images/copy-icon.svg";
import { ReactComponent as RefreshIcon } from "assets/images/refresh-icon.svg";

import analytics from "services/analytics";
import { apiURL, apiSwaggerURL } from "routes/routes";
import { AnalyticsEvents } from "shared/constants/analytics";

const ApiTokenMain: React.FC = () => {
  const dispatch = useAppDispatch();

  const [contentLoading, setContentLoading] = useState(true);
  const [buttonLoading, setButtonLoading] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  const token = useAppSelector((state) => state.clientApiKey.key);

  useEffect(() => {
    // Fetch token on tab click
    dispatch(getClientApiKey()).finally(() => setContentLoading(false));
  }, []);

  const onCreateApiToken = async () => {
    analytics().sendEvent(AnalyticsEvents.api_token_create_token);
    setButtonLoading(true);

    try {
      await dispatch(createClientApiKey()).unwrap();
    } finally {
      setButtonLoading(false);
    }
  };

  const onUpdateApiToken = () => {
    analytics().sendEvent(AnalyticsEvents.api_token_regenerate_token);
    setShowUpdateModal(true);
  };

  const onDeleteApiToken = async () => {
    analytics().sendEvent(AnalyticsEvents.api_token_delete_token);
    setShowDeleteModal(true);
  };

  const onCopy = async () => {
    analytics().sendEvent(AnalyticsEvents.api_token_copy_token);
    try {
      if (token?.token) {
        token?.token && (await navigator.clipboard.writeText(token.token));

        dispatch(
          setNotification({
            message: "Токен скопійовано",
            severity: "success",
          })
        );
      }
    } catch (err) {
      dispatch(
        setNotification({ message: "Помилка копіювання", severity: "error" })
      );
    }
  };

  return (
    <>
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        width="100%"
        sx={{ mb: { xs: 2, sm: 3 } }}
      >
        <Box sx={{ mb: { xs: 2, sm: 0 } }}>
          <Typography sx={{ mb: 0.5 }} variant="h3">
            API Токен
          </Typography>
          <Typography variant="body1" color="grey700">
            API Токен дозволяє інтегрувати оплату за IBAN з іншими сервісами —
            зберігайте його в таємниці
          </Typography>
        </Box>
        {!contentLoading && !token && (
          <Button
            variant="contained"
            color="primary"
            onClick={onCreateApiToken}
            loading={buttonLoading}
            disabled={contentLoading || buttonLoading}
          >
            Згенерувати Токен
          </Button>
        )}
      </Box>
      {!contentLoading && !token ? (
        <EmptyBlock text="Ви ще не маєте створеного API Токена" />
      ) : contentLoading ? (
        <Skeleton animation="wave" height={56} width="70%" />
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "flex-end" },
            gap: 1.5,
            flexDirection: { xs: "column", md: "row" },
            width: "100%",
            overflow: "hidden",
          }}
        >
          <Box sx={{ width: { xs: "100%", md: "60%" } }}>
            <Typography variant="subtitle2" color="grey800" sx={{ mb: 1 }}>
              Токен
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                p: 1,
                pl: 2,
                bgcolor: "#EDEDED",
                borderRadius: 1,
              }}
            >
              <Typography noWrap data-posthog-mask="true">
                {token?.token}
              </Typography>
              <ButtonBase
                sx={{ p: 1, borderRadius: 3 }}
                onClick={onUpdateApiToken}
              >
                <RefreshIcon />
                <Typography variant="body2" sx={{ ml: 0.5 }}>
                  Перегенерувати
                </Typography>
              </ButtonBase>
              <ButtonBase sx={{ p: 1, borderRadius: 3 }} onClick={onCopy}>
                <CopyIcon />
                <Typography
                  variant="body2"
                  sx={{ ml: 0.5, display: { xs: "none", md: "block" } }}
                >
                  Копіювати
                </Typography>
              </ButtonBase>
            </Box>
          </Box>
          <Button
            variant="contained"
            color="error"
            onClick={onDeleteApiToken}
            withBorder
          >
            Видалити Токен
          </Button>
        </Box>
      )}
      <Box sx={{ mt: 4 }}>
        <Typography color="grey700">
          API IBAN oplata доступне за адресою:{" "}
          <Link sx={{ display: "inline-block" }} href={apiURL} variant="body1">
            {apiURL}
          </Link>
        </Typography>
        <Typography color="grey700" sx={{ mt: 0.5 }}>
          Swagger-документація знаходиться{" "}
          <Link variant="body1" href={apiSwaggerURL}>
            тут
          </Link>
        </Typography>
      </Box>

      <TokenDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
      />
      <TokenUpdateModal
        isOpen={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
      />
    </>
  );
};

export default ApiTokenMain;
